// pages/Orders/AdminInstallations.js
import React, { useState, useEffect } from 'react';
import './AdminInstallations.css';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://transcontinentalnetwork.com/transcontinental-api';

const getAuthToken = () => localStorage.getItem('access_token');

const AdminInstallations = ({ setCurrentPage }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [scheduleForm, setScheduleForm] = useState({
    installation_date: '',
    installation_time_slot: '09:00-12:00',
    technician_name: '',
    technician_phone: '',
    installation_notes: ''
  });

  useEffect(() => {
    fetchInstallationOrders();
  }, [filterStatus]);

  const fetchInstallationOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      const response = await fetch(`${API_BASE_URL}/orders/installations?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setOrders(data.data.orders || []);
      } else {
        setError(data.message || 'Failed to fetch installation orders');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load installation orders');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleInstallation = async (e) => {
    e.preventDefault();

    if (!selectedOrder) return;

    try {
      const token = getAuthToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/orders/${selectedOrder.id}/installation/schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scheduleForm)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('Installation scheduled successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setShowScheduleModal(false);
        resetScheduleForm();
        fetchInstallationOrders();
      } else {
        setError(data.message || 'Failed to schedule installation');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to schedule installation');
    }
  };

  const handleUpdateInstallationStatus = async (orderId, newStatus) => {
    try {
      const token = getAuthToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/installation/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          notes: `Status updated to ${newStatus} by admin`
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage(`Installation status updated to ${newStatus}`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchInstallationOrders();
      } else {
        setError(data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to update installation status');
    }
  };

  const handleSetEstimatedDelivery = async (orderId) => {
    const date = prompt('Enter estimated delivery date (YYYY-MM-DD):');
    if (!date) return;

    try {
      const token = getAuthToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/delivery/estimate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          estimated_delivery_date: date
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('Estimated delivery date set successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchInstallationOrders();
      } else {
        setError(data.message || 'Failed to set delivery date');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to set delivery date');
    }
  };

  const openScheduleModal = (order) => {
    setSelectedOrder(order);
    setShowScheduleModal(true);
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      installation_date: '',
      installation_time_slot: '09:00-12:00',
      technician_name: '',
      technician_phone: '',
      installation_notes: ''
    });
    setSelectedOrder(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return `$${parseFloat(price || 0).toFixed(2)}`;
  };

  const getInstallationStatusClass = (status) => {
    const classes = {
      pending: 'install-status-pending',
      scheduled: 'install-status-scheduled',
      in_progress: 'install-status-in-progress',
      completed: 'install-status-completed',
      cancelled: 'install-status-cancelled'
    };
    return classes[status?.toLowerCase()] || 'install-status-pending';
  };

  if (loading) {
    return (
      <div className="admin-installations-page">
        <LoadingSpinner type="page" message="Loading installation orders..." />
      </div>
    );
  }

  return (
    <div className="admin-installations-page">
      <div className="installations-header">
        <h1>📦 Installation Management</h1>
        <p>Manage and schedule installation services</p>
      </div>

      {successMessage && (
        <div className="success-message">
          ✓ {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          ⚠️ {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="installations-controls">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All
          </button>
          <button
            className={`filter-tab ${filterStatus === 'pending' ? 'active' : ''}`}
            onClick={() => setFilterStatus('pending')}
          >
            Pending
          </button>
          <button
            className={`filter-tab ${filterStatus === 'scheduled' ? 'active' : ''}`}
            onClick={() => setFilterStatus('scheduled')}
          >
            Scheduled
          </button>
          <button
            className={`filter-tab ${filterStatus === 'in_progress' ? 'active' : ''}`}
            onClick={() => setFilterStatus('in_progress')}
          >
            In Progress
          </button>
          <button
            className={`filter-tab ${filterStatus === 'completed' ? 'active' : ''}`}
            onClick={() => setFilterStatus('completed')}
          >
            Completed
          </button>
        </div>

        <button className="btn-refresh" onClick={fetchInstallationOrders}>
          ↻ Refresh
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="no-installations">
          <p>📭 No installation orders found</p>
        </div>
      ) : (
        <div className="installations-grid">
          {orders.map((order) => (
            <div key={order.id} className="installation-card">
              <div className="installation-card-header">
                <h3>Order #{order.order_number}</h3>
                <span className={`install-badge ${getInstallationStatusClass(order.installation?.installation_status)}`}>
                  {order.installation?.installation_status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <div className="installation-card-body">
                <div className="install-info-row">
                  <span className="info-label">Customer:</span>
                  <span className="info-value">{order.customer_name}</span>
                </div>

                <div className="install-info-row">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{order.customer_phone}</span>
                </div>

                <div className="install-info-row">
                  <span className="info-label">Order Date:</span>
                  <span className="info-value">{formatDate(order.created_at)}</span>
                </div>

                <div className="install-info-row">
                  <span className="info-label">Total:</span>
                  <span className="info-value">{formatPrice(order.total_amount)}</span>
                </div>

                {order.installation?.installation_date && (
                  <>
                    <div className="install-info-row highlight">
                      <span className="info-label">Installation Date:</span>
                      <span className="info-value">{formatDate(order.installation.installation_date)}</span>
                    </div>

                    <div className="install-info-row">
                      <span className="info-label">Time Slot:</span>
                      <span className="info-value">{order.installation.installation_time_slot_label}</span>
                    </div>
                  </>
                )}

                {order.installation?.technician && (
                  <div className="install-info-row">
                    <span className="info-label">Technician:</span>
                    <span className="info-value">{order.installation.technician.name}</span>
                  </div>
                )}

                {order.installation?.estimated_delivery_date && (
                  <div className="install-info-row">
                    <span className="info-label">Est. Delivery:</span>
                    <span className="info-value">{formatDate(order.installation.estimated_delivery_date)}</span>
                  </div>
                )}
              </div>

              <div className="installation-card-footer">
                {order.installation?.installation_status === 'pending' && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => openScheduleModal(order)}
                  >
                    📅 Schedule
                  </button>
                )}

                {order.installation?.installation_status === 'scheduled' && (
                  <>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleUpdateInstallationStatus(order.id, 'in_progress')}
                    >
                      ▶️ Start
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => openScheduleModal(order)}
                    >
                      🔄 Reschedule
                    </button>
                  </>
                )}

                {order.installation?.installation_status === 'in_progress' && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleUpdateInstallationStatus(order.id, 'completed')}
                  >
                    ✅ Complete
                  </button>
                )}

                <button
                  className="btn btn-info btn-sm"
                  onClick={() => handleSetEstimatedDelivery(order.id)}
                >
                  📦 Set Delivery
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Schedule Installation</h2>
              <button onClick={() => setShowScheduleModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleScheduleInstallation}>
                <div className="form-group">
                  <label>Order Number:</label>
                  <input type="text" value={selectedOrder.order_number} disabled />
                </div>

                <div className="form-group">
                  <label>Customer:</label>
                  <input type="text" value={selectedOrder.customer_name} disabled />
                </div>

                <div className="form-group">
                  <label>Installation Date *</label>
                  <input
                    type="datetime-local"
                    value={scheduleForm.installation_date}
                    onChange={(e) => setScheduleForm({...scheduleForm, installation_date: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Time Slot *</label>
                  <select
                    value={scheduleForm.installation_time_slot}
                    onChange={(e) => setScheduleForm({...scheduleForm, installation_time_slot: e.target.value})}
                    required
                  >
                    <option value="09:00-12:00">Morning (9 AM - 12 PM)</option>
                    <option value="12:00-15:00">Afternoon (12 PM - 3 PM)</option>
                    <option value="15:00-18:00">Evening (3 PM - 6 PM)</option>
                    <option value="09:00-18:00">Anytime (9 AM - 6 PM)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Technician Name</label>
                  <input
                    type="text"
                    value={scheduleForm.technician_name}
                    onChange={(e) => setScheduleForm({...scheduleForm, technician_name: e.target.value})}
                    placeholder="Enter technician name"
                  />
                </div>

                <div className="form-group">
                  <label>Technician Phone</label>
                  <input
                    type="tel"
                    value={scheduleForm.technician_phone}
                    onChange={(e) => setScheduleForm({...scheduleForm, technician_phone: e.target.value})}
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="form-group">
                  <label>Installation Notes</label>
                  <textarea
                    value={scheduleForm.installation_notes}
                    onChange={(e) => setScheduleForm({...scheduleForm, installation_notes: e.target.value})}
                    placeholder="Any special notes or instructions"
                    rows="3"
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowScheduleModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Schedule Installation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="installations-footer">
        <button className="btn btn-back" onClick={() => setCurrentPage('admin-dashboard')}>
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default AdminInstallations;